import { AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import {
  BookkeepingCycle,
  WorkItem,
  WorkItemCompletion,
} from 'jobs/bookkeeping/BookkeepingProgressionInfo'
import BookkeepingProgressionStore from 'jobs/bookkeeping/BookkeepingProgressionStore'
import { isNil } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { Id } from 'types/definitions'
import { required, validationContext } from 'validx'

export class CorrectWorkItemViewModel {
  @observable workItemId = Id.none
  @observable name = ''
  @observable description = ''
  @observable completedCount = 0
  @observable correctAmount = 0
  @observable maxCorrectAmount = 0

  readonly fieldLengths = {
    correctAmount: { min: 0 },
  } as const

  /**
   * Validation context.
   */
  readonly validation = validationContext(this, {
    correctAmount: [
      required(),
      () =>
        this.correctedItemsAreValid ||
        `The number of corrected items must be between ${this.fieldLengths.correctAmount.min} and ${this.maxCorrectAmount}.`,
    ],
  })

  /**
   * Whether the corrected items quantity is valid.
   */
  @computed
  get correctedItemsAreValid() {
    return (
      this.correctAmount <= this.maxCorrectAmount &&
      this.correctAmount >= this.fieldLengths.correctAmount.min
    )
  }

  constructor(workItem: WorkItem, workItemCompletion?: WorkItemCompletion) {
    makeObservable(this)

    const completedCount = workItemCompletion?.completedCount || 0

    this.workItemId = workItem.workItemId
    this.name = workItem.inventoryItemName
    this.completedCount = completedCount
    this.correctAmount = completedCount
    this.description = workItem.inventoryItemDescription
    this.maxCorrectAmount = workItem.remaining + completedCount
  }

  /**
   * Resets the fields.
   */
  @action.bound
  reset() {
    this.correctAmount = this.completedCount
  }

  /**
   * Sets the number of completed items.
   * @param value
   */
  @action.bound
  setCorrectedItems(value: number | null) {
    if (!isNil(value)) {
      this.correctAmount = value
    }
  }
}

export default class BookkeepingCorrectCycleDialogViewModel extends ModalViewModel {
  @observable selectedCycle = BookkeepingCycle.none

  constructor(
    private newAlert: AlertFactory,
    private jobId: Id,
    private bookkeepingProgressionStore: BookkeepingProgressionStore,
    private returnToList: () => void,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Returns the bookkeeping progression info.
   */
  @computed
  get bookkeepingProgressionInfo() {
    return this.bookkeepingProgressionStore.bookkeepingProgressions.get(
      this.jobId,
    )!
  }

  /**
   * The corrected items.
   */
  @computed
  get correctedItems() {
    return (this.bookkeepingProgressionInfo?.workItems || []).map(
      (workItem) => {
        const completeItem = (
          this.selectedCycle.workItemsCompletion || []
        )?.find((ci) => ci.id === workItem.workItemId)
        return new CorrectWorkItemViewModel(workItem, completeItem)
      },
    )
  }

  /**
   * Validates each corrected item.
   * @returns
   */
  @action.bound
  validate() {
    return (
      this.correctedItems.every(
        (ci) => ci.validation.reset().validate().isValid,
      ) && this.correctedItems.some((ci) => ci.correctAmount >= 0)
    )
  }

  /**
   * Resets each corrected item.
   */
  @action.bound
  reset() {
    this.correctedItems.forEach((ci) => ci.reset())
  }

  /**
   * Sets the cycle to be corrected and shows the dialog.
   * @param selectedCycle
   */
  @action.bound
  open(selectedCycle: BookkeepingCycle) {
    this.selectedCycle = selectedCycle
    this.show()
  }

  /**
   * Corrects the current cycle.
   */
  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    try {
      await this.bookkeepingProgressionStore.correctBookkeepingCycle({
        jobId: this.jobId,
        cycleId: this.selectedCycle.id,
        correctedItems: this.correctedItems.map((ci) => ({
          id: ci.workItemId,
          completedCount: ci.correctAmount,
        })),
      })

      this.newAlert()
        .withState(AlertState.Success)
        .saying(`Bookkeeping cycle ${this.selectedCycle.number} corrected.`)
        .show()
        .autoDismiss()

      this.reset()
      this.close()
      this.returnToList()
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  })
}
